import React, { useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../state/AuthContext';

const Header: React.FC = () => {
  const { user, roleMeta, logout } = useAuth();
  const today = new Intl.DateTimeFormat('en', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date());

  const initials = useMemo(() => {
    if (!user?.name) return 'LK';
    const parts = user.name.trim().split(/\s+/);
    return parts
      .map((part) => part[0]?.toUpperCase())
      .filter(Boolean)
      .slice(0, 2)
      .join('');
  }, [user?.name]);

  return (
    <header className="sticky top-0 z-30 border-b border-white/60 bg-white/80 backdrop-blur">
      <div className="flex flex-col gap-4 px-6 py-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">{today}</p>
          <h1 className="text-2xl font-semibold text-slate-900">Welcome back, Dr. Chen</h1>
          <p className="text-sm text-slate-500">
            Monitor hospital operations and coordinate today&apos;s care.
          </p>
        </div>

        <div className="flex flex-1 items-center gap-4 md:flex-none">
          <div className="relative flex-1 md:flex-none">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              placeholder="Search patients, cases, or vets"
              className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-700 shadow-sm focus:border-brand-blue focus:ring-brand-blue"
            />
          </div>
          <button
            type="button"
            className="relative rounded-xl border border-slate-200 bg-white p-2 text-slate-500 shadow-sm hover:text-brand-blue"
            aria-label="Notifications"
          >
            <BellIcon className="h-5 w-5" />
            <span className="absolute -right-1 -top-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[10px] font-semibold text-white">
              4
            </span>
          </button>
          <div className="flex items-center gap-3 rounded-2xl border border-white/80 bg-white px-4 py-2 shadow-soft">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-brand-blue to-brand-sky text-white ring-4 ring-white">
              <div className="flex h-full w-full items-center justify-center text-lg font-semibold">
                {initials}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">{user?.name ?? 'Team member'}</p>
              {roleMeta && <p className={`text-xs font-semibold ${roleMeta.accent}`}>{roleMeta.label}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 shadow-soft transition hover:border-rose-200 hover:text-rose-600"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
